/**
 * # 背景音乐：SpringFestival（春节序曲）
 *
 * 改编自中国经典民乐《春节序曲》的秧歌调主题段落， 以方波音色配合轻断奏，模仿唢呐和秧歌的热闹欢腾感， 营造浓厚的春节节日氛围。
 *
 * ## 曲式结构
 *
 * | 段落   | 风格     | 说明                           |
 * | ------ | -------- | ------------------------------ |
 * | 第一句 | 秧歌调   | 经典旋律，中等节奏             |
 * | 第二句 | 欢腾段落 | 短促音符密集交替，情绪高涨     |
 * | 第三句 | 再现秧歌 | 主题再现，音区拔高更热烈       |
 * | 第四句 | 锣鼓模仿 | 高低音交替，穿插休止符模仿锣鼓 |
 *
 * ## 音乐参数
 *
 * | 参数     | 值     | 说明                               |
 * | -------- | ------ | ---------------------------------- |
 * | duration | 280    | 每个音符的基准时长（ms），较快节奏 |
 * | volume   | 0.08   | 音量（0-1）                        |
 * | wave     | square | 波形（方波，模拟唢呐/秧歌）        |
 * | gate     | 0.7    | 连奏/断奏比例，轻断奏颗粒分明      |
 *
 * ## 运音包络
 *
 * | 参数         | 值    | 说明                      |
 * | ------------ | ----- | ------------------------- |
 * | attackTime   | 0.003 | 起音时间（3ms，快速起音） |
 * | releaseTime  | 0.02  | 释音时间（20ms）          |
 * | sustainRatio | 0.5   | 延音比（较低，音符跳跃）  |
 *
 * @constant {object} SpringFestival
 */
const SpringFestival = {
  /** ## 音乐名称 */
  name: 'Spring Festival',

  /**
   * ## 旋律数据
   *
   * 每个音符包含：
   *
   * - `freq`：频率（Hz），0 表示休止符
   * - `dur`：时长系数（乘以 duration 得到实际时长）
   *
   * @type {{ freq: number; dur: number }[]}
   */
  melody: [
    // ===== 第一句：秧歌调 =====
    { freq: 523, dur: 0.6 }, // 啦 (C5)
    { freq: 587, dur: 0.3 }, // 啦 (D5)
    { freq: 659, dur: 0.9 }, // 啦～ (E5)
    { freq: 659, dur: 0.6 }, // 啦
    { freq: 784, dur: 0.3 }, // 啦
    { freq: 880, dur: 1.2 }, // 啦～ (A5)

    { freq: 880, dur: 0.6 }, // 啦
    { freq: 784, dur: 0.3 }, // 啦
    { freq: 659, dur: 0.9 }, // 啦～
    { freq: 587, dur: 0.6 }, // 啦
    { freq: 523, dur: 1.5 }, // 啦～

    // ===== 第二句：欢腾段落 =====
    { freq: 659, dur: 0.3 },
    { freq: 659, dur: 0.3 },
    { freq: 784, dur: 0.3 },
    { freq: 784, dur: 0.3 },
    { freq: 880, dur: 0.3 },
    { freq: 880, dur: 0.3 },
    { freq: 784, dur: 0.6 },
    { freq: 659, dur: 1.2 },

    { freq: 587, dur: 0.3 },
    { freq: 587, dur: 0.3 },
    { freq: 659, dur: 0.3 },
    { freq: 659, dur: 0.3 },
    { freq: 784, dur: 0.3 },
    { freq: 784, dur: 0.3 },
    { freq: 659, dur: 0.6 },
    { freq: 523, dur: 1.2 },

    // ===== 第三句：再现秧歌，更热烈 =====
    { freq: 523, dur: 0.4 },
    { freq: 587, dur: 0.2 },
    { freq: 659, dur: 0.6 },
    { freq: 659, dur: 0.4 },
    { freq: 784, dur: 0.2 },
    { freq: 880, dur: 0.8 },

    { freq: 1047, dur: 0.4 }, // 拔高 (C6)
    { freq: 880, dur: 0.2 },
    { freq: 784, dur: 0.6 },
    { freq: 659, dur: 0.4 },
    { freq: 587, dur: 0.4 },
    { freq: 523, dur: 2 }, // 收束

    // ===== 第四句：锣鼓模仿 =====
    { freq: 659, dur: 0.2 },
    { freq: 659, dur: 0.2 },
    { freq: 0, dur: 0.1 }, // 休止符模仿锣鼓间隙
    { freq: 659, dur: 0.2 },
    { freq: 0, dur: 0.1 },
    { freq: 784, dur: 0.2 },
    { freq: 784, dur: 0.2 },
    { freq: 0, dur: 0.1 },
    { freq: 784, dur: 0.2 },
    { freq: 0, dur: 0.1 },
    { freq: 659, dur: 0.4 },
    { freq: 587, dur: 0.4 },
    { freq: 523, dur: 0.8 },

    { freq: 0, dur: 1 }, // 段落呼吸
  ],

  /** ## 每个音符的基准时长（ms），较快节奏 */
  duration: 280,

  /** ## 音量（0-1） */
  volume: 0.11,

  /** ## 波形类型：方波（模拟唢呐/秧歌的热闹感） */
  wave: 'square',

  /** ## 连奏/断奏比例（0.7，轻断奏，颗粒分明） */
  gate: 0.7,

  /** ## 运音包络 */
  articulation: {
    /** ## 起音时间（3ms，快速起音） */
    attackTime: 0.003,
    /** ## 释音时间（20ms） */
    releaseTime: 0.02,
    /** ## 延音比（0.5，较低，音符跳跃） */
    sustainRatio: 0.5,
  },
};

export default SpringFestival;
